import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

export type LegalDocType = 'impressum' | 'agb' | 'datenschutz' | null;

interface LegalModalProps {
    type: LegalDocType;
    onClose: () => void;
}

export function LegalModal({ type, onClose }: LegalModalProps) {
    const content = {
        impressum: {
            title: 'Legal Notice',
            body: (
                <div className="space-y-6 text-sm text-neutral-400 font-light font-sans leading-relaxed">
                    <p><strong className="text-white">Legal Notice & Company Details</strong></p>
                    <p>
                        Bertan Yalcintepe - Emilia Ivanova Industries<br />
                        1. Firin Sokak, Nr.10, Yeni Mahalle - Yildirim<br />
                        D3<br />
                        Bursa, Bursa / Yildirim 16360<br />
                        Turkey
                    </p>

                    <p><strong className="text-white">Represented by:</strong><br />
                        Bertan Yalcintepe
                    </p>

                    <p><strong className="text-white">Contact:</strong><br />
                        Phone: +90 530 113 39 74<br />
                        Email: hello@emilia-ivanova.com<br />
                        Website: https://emilia-ivanova.com/
                    </p>

                    <p><strong className="text-white">Disclaimer of Liability</strong><br />
                        As a service provider, we are responsible for our own content on these pages under general laws. However, we are not obligated to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity. Liability for damages, regardless of the legal basis, is limited to intent and gross negligence.
                    </p>

                    <p><strong className="text-white">Copyright</strong><br />
                        The content, brands (especially "Emilia Ivanova"), and works created by us on these pages are subject to copyright law. Duplication, processing, distribution, and any kind of exploitation outside the limits of copyright require our express written consent.
                    </p>
                </div>
            )
        },
        agb: {
            title: 'Terms of Use (Website)',
            body: (
                <div className="space-y-6 text-sm text-neutral-400 font-light font-sans leading-relaxed">
                    <p><strong className="text-white">1. Scope</strong><br />
                        These Terms of Use apply exclusively to the access and use of this informational and portfolio website. They strictly do <strong>not</strong> cover or govern Software-as-a-Service (SaaS) agreements, software licenses, or specific services (such as Replyra or Replyra Creator). These external products operate on separate platforms and are governed by their own specific terms and conditions and/or individual enterprise contracts.
                    </p>
                    <p><strong className="text-white">2. Provision of Information</strong><br />
                        The provider offers information on this website regarding corporate identity, visions, team structure, and a broad portfolio of the corporate group. There is no legal entitlement to the permanent availability of this information or the functionality of the website.
                    </p>
                    <p><strong className="text-white">3. Business Initiation</strong><br />
                        The use of contact forms ("Initiate Protocol") serves exclusively for non-binding communication and business initiation. No automated contractual relationship arises from this. Any B2B business collaboration requires a separate, written contract.
                    </p>
                    <p><strong className="text-white">4. Intellectual Property & Copy Protection</strong><br />
                        All content published on this website, in particular the "Emilia Ivanova" brand, provided concept graphics, and program codes, are protected by copyright. Unauthorized commercial use is strictly prohibited. Direct downloading or copying of content is forbidden and technically blocked.
                    </p>
                    <p><strong className="text-white">5. Limitation of Liability</strong><br />
                        The provider shall only be liable for intent and gross negligence arising directly from the use of this portfolio page. No liability is assumed on this page for indirect damages, lost profits, or damages resulting from the use of external SaaS platforms merely linked or referred to here.
                    </p>
                </div>
            )
        },
        datenschutz: {
            title: 'Privacy Policy',
            body: (
                <div className="space-y-6 text-sm text-neutral-400 font-light font-sans leading-relaxed">
                    <p><strong className="text-white">1. Data Protection at a Glance</strong><br />
                        We take the protection of your personal data very seriously. We treat your personal data confidentially and in accordance with statutory data protection regulations as well as this privacy policy.
                    </p>
                    <p><strong className="text-white">2. Data Collection on This Website</strong><br />
                        Data processing on this website is carried out by the website operator. Furthermore, we collect your data when you provide it to us, e.g., by filling out the "Initiate Protocol" (contact form). Form data is used strictly confidentially for the initiation of business contacts.
                    </p>
                    <p><strong className="text-white">3. Analysis Tools and Third-Party Tools</strong><br />
                        When visiting this website, your surfing behavior may be statistically evaluated. This happens primarily through cookies and so-called analysis programs. The analysis of your surfing behavior is generally anonymous.
                    </p>
                    <p><strong className="text-white">4. SSL / TLS Encryption</strong><br />
                        This site uses TLS encryption for security reasons and to protect the transmission of confidential content. You can recognize an encrypted connection by the fact that the browser address line changes from "http://" to "https://" and by the lock symbol in your browser line.
                    </p>
                    <p><strong className="text-white">5. Rights of the Data Subject</strong><br />
                        You have the right at any time to request information free of charge about the origin, recipient, and purpose of your stored personal data. You also have a right to request the correction or deletion of this data.
                    </p>
                </div>
            )
        }
    };

    const selectedContent = type ? content[type] : null;

    return (
        <AnimatePresence>
            {type && selectedContent && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/80 backdrop-blur-md"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="relative w-full max-w-2xl max-h-[80vh] overflow-y-auto bg-[#0a0a0a] border border-white/10 p-8 md:p-12 shadow-[0_0_50px_rgba(0,0,0,0.5)] custom-scrollbar"
                    >
                        <button
                            onClick={onClose}
                            className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors"
                        >
                            <X size={24} />
                        </button>

                        <h2 className="font-serif text-3xl md:text-4xl text-white mb-8 border-b border-white/10 pb-6 uppercase tracking-widest">
                            {selectedContent.title}
                        </h2>

                        <div className="text-left">
                            {selectedContent.body}
                        </div>

                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
